import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { motion, useReducedMotion } from "framer-motion";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { Sheet, SheetContent, SheetTrigger, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { X, Flag, ChevronLeft, ChevronRight, LayoutGrid } from "lucide-react";

const fmtTime = (s) => {
  const m = Math.floor(s / 60);
  const sec = s % 60;
  return `${String(m).padStart(2, "0")}:${String(sec).padStart(2, "0")}`;
};

const modeTitle = { full: "Esame casuale", simulation: "Simulazione ufficiale", block: "Simulazione blocco", module: "Quiz modulo", review: "Ripasso errori", sample: "Simulazione campione" };

export default function Esame() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const mode = params.get("mode") || "full";
  const blocco = params.get("blocco");
  const moduleId = params.get("moduleId");
  const simulationId = params.get("simulationId");

  const [session, setSession] = useState(null);
  const [error, setError] = useState(null);
  const [answers, setAnswers] = useState({});
  const [flagged, setFlagged] = useState({});
  const [idx, setIdx] = useState(0);
  const [timeLeft, setTimeLeft] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [exitOpen, setExitOpen] = useState(false);
  const [gridOpen, setGridOpen] = useState(false);
  const [liveMessage, setLiveMessage] = useState("");
  const startedRef = useRef(false);
  const submittedRef = useRef(false);
  const warnedRef = useRef(false);
  const reduceMotion = useReducedMotion();

  // Start session
  useEffect(() => {
    if (startedRef.current) return;
    startedRef.current = true;
    const body = { mode };
    if (mode === "block") body.blocco = Number(blocco);
    if (mode === "review" && blocco) body.blocco = Number(blocco);
    if (mode === "module") body.moduleId = moduleId;
    if (mode === "simulation") body.simulationId = simulationId;
    api
      .post("/exams/start", body)
      .then((r) => {
        setSession(r.data);
        setTimeLeft(r.data.durationSeconds);
      })
      .catch((e) => setError(errMsg(e, "Impossibile avviare la simulazione.")));
  }, [mode, blocco, moduleId, simulationId]);

  const doSubmit = useCallback(
    async (auto = false) => {
      if (!session || submittedRef.current) return;
      submittedRef.current = true;
      setSubmitting(true);
      try {
        const timeTaken = session.durationSeconds - (timeLeft ?? 0);
        const r = await api.post(`/exams/${session.sessionId}/submit`, {
          answers,
          timeTakenSeconds: Math.max(0, timeTaken),
        });
        navigate("/risultati", { state: { result: r.data }, replace: true });
      } catch (e) {
        submittedRef.current = false;
        setSubmitting(false);
        setError(errMsg(e, "Consegna non riuscita. Riprova."));
      }
    },
    [session, answers, timeLeft, navigate]
  );

  // Timer
  useEffect(() => {
    if (timeLeft === null || submittedRef.current) return;
    if (timeLeft <= 0) {
      doSubmit(true);
      return;
    }
    const t = setTimeout(() => setTimeLeft((s) => s - 1), 1000);
    return () => clearTimeout(t);
  }, [timeLeft, doSubmit]);

  // 5-minute warning (once)
  useEffect(() => {
    if (timeLeft === null || warnedRef.current) return;
    if (timeLeft <= 300 && timeLeft > 0 && session && session.durationSeconds > 300) {
      warnedRef.current = true;
      toast.warning("Ultimi 5 minuti. Controlla le domande segnate.", { duration: 5000 });
      setLiveMessage("Attenzione: mancano 5 minuti alla fine del tempo.");
    }
  }, [timeLeft, session]);

  if (error && !session) {
    return (
      <div className="min-h-dvh bg-[color:var(--performance)] text-white flex flex-col items-center justify-center px-6 text-center performance-shell">
        <p className="font-extrabold text-lg">Non si parte.</p>
        <p className="mt-2 text-sm text-white/60 max-w-sm leading-6" data-testid="exam-error-message">{error}</p>
        <button
          onClick={() => navigate("/simulazioni")}
          data-testid="exam-error-back-button"
          className="mt-6 rounded-full bg-[color:var(--volt)] text-[color:var(--ink)] font-extrabold px-6 py-3 hover:brightness-95 transition-all duration-200"
        >
          Torna alle simulazioni
        </button>
      </div>
    );
  }

  if (!session) {
    return (
      <div className="min-h-dvh bg-[color:var(--performance)] text-white flex flex-col items-center justify-center performance-shell">
        <p className="font-mono text-sm tracking-[0.3em] uppercase text-white/60">Preparazione…</p>
      </div>
    );
  }

  const questions = session.questions;
  const q = questions[idx];
  const answeredCount = Object.keys(answers).length;
  const unanswered = questions.length - answeredCount;
  const low = timeLeft !== null && timeLeft <= 300;

  const selectAnswer = (i) => setAnswers((a) => ({ ...a, [q.id]: i }));
  const toggleFlag = () => setFlagged((f) => ({ ...f, [q.id]: !f[q.id] }));

  return (
    <div className="min-h-dvh bg-[color:var(--performance)] text-white flex flex-col performance-shell" style={{ paddingTop: "env(safe-area-inset-top)" }}>
      {/* Top bar */}
      <header className="px-4 pt-4 pb-3 border-b border-white/10">
        <div className="max-w-2xl mx-auto">
          <div className="flex items-center justify-between">
            <button
              onClick={() => setExitOpen(true)}
              aria-label="Esci dalla simulazione"
              data-testid="exam-exit-button"
              className="w-10 h-10 rounded-full border border-white/15 flex items-center justify-center hover:bg-white/10 transition-colors duration-200"
            >
              <X className="w-4 h-4" />
            </button>
            <div className="text-center">
              <p className="font-mono text-[10px] tracking-[0.25em] uppercase text-white/50">{modeTitle[session.mode]}</p>
              <p className={`font-mono text-4xl sm:text-5xl font-semibold tracking-[-0.04em] mt-0.5 ${low ? "timer-low" : ""}`} data-testid="exam-timer" role="timer" aria-label={`Tempo rimanente ${fmtTime(timeLeft ?? 0)}`}>
                {fmtTime(timeLeft ?? 0)}
              </p>
            </div>
            <div className="w-10 text-right">
              <p className="font-mono text-xs text-white/60" data-testid="exam-progress-count">
                {answeredCount}/{questions.length}
              </p>
            </div>
          </div>
          <div className="mt-3 flex items-center justify-between">
            <span className="font-mono text-[11px] tracking-widest uppercase text-[color:var(--volt)]" data-testid="exam-current-block">
              Blocco {q.blocco}
            </span>
            <span className="font-mono text-[11px] text-white/50">soglia 9/15 · domanda {idx + 1}/{questions.length}</span>
          </div>
          {/* Animated answered-progress bar */}
          <div
            className="mt-3 h-1 rounded-full bg-white/10 overflow-hidden"
            role="progressbar"
            aria-valuemin={0}
            aria-valuemax={questions.length}
            aria-valuenow={answeredCount}
            aria-label={`Risposte date: ${answeredCount} su ${questions.length}`}
            data-testid="exam-progress-bar"
          >
            <div
              className="h-full rounded-full bg-[color:var(--volt)] transition-[width] duration-300 ease-out"
              style={{ width: `${(answeredCount / questions.length) * 100}%` }}
            />
          </div>
        </div>
      </header>

      {/* Screen-reader announcements (time warning) */}
      <div aria-live="polite" className="sr-only-live">{liveMessage}</div>

      {/* Question */}
      <main className="flex-1 px-4 py-6 overflow-y-auto">
        <div className="max-w-2xl mx-auto">
          <motion.div
            key={q.id}
            initial={reduceMotion ? false : { opacity: 0, x: 10 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.22, ease: [0.2, 0.8, 0.2, 1] }}
          >
            <p className="text-lg sm:text-xl font-bold leading-7 tracking-tight" data-testid="exam-question-text">
              {q.testo}
            </p>
            <div className="mt-6 space-y-3" role="radiogroup" aria-label={`Risposte alla domanda ${idx + 1}`}>
              {q.opzioni.map((opt, i) => {
                const selected = answers[q.id] === i;
                return (
                  <button
                    key={i}
                    onClick={() => selectAnswer(i)}
                    data-testid="exam-answer-option"
                    role="radio"
                    aria-checked={selected}
                    className={`press w-full text-left rounded-2xl border px-4 py-4 min-h-[52px] text-sm leading-6 transition-colors duration-200 ${
                      selected
                        ? "border-[color:var(--volt)] bg-[color:var(--volt)] text-[color:var(--ink)] font-bold"
                        : "border-white/12 bg-white/5 hover:bg-white/10"
                    }`}
                  >
                    <span className="font-mono text-xs mr-3 opacity-60">{String.fromCharCode(65 + i)}</span>
                    {opt}
                  </button>
                );
              })}
            </div>
          </motion.div>

          <button
            onClick={toggleFlag}
            data-testid="exam-flag-question-button"
            aria-pressed={!!flagged[q.id]}
            className={`press mt-5 inline-flex items-center gap-2 rounded-full border px-4 py-2.5 min-h-[44px] text-xs font-bold uppercase tracking-wide transition-colors duration-200 ${
              flagged[q.id] ? "border-[color:var(--volt)] text-[color:var(--volt)]" : "border-white/15 text-white/60 hover:text-white"
            }`}
          >
            <Flag className="w-3.5 h-3.5" fill={flagged[q.id] ? "currentColor" : "none"} />
            {flagged[q.id] ? "Segnata — torna dopo" : "Segna e torna dopo"}
          </button>
        </div>
      </main>

      {/* Bottom controls */}
      <footer className="px-4 py-4 border-t border-white/10" style={{ paddingBottom: "calc(env(safe-area-inset-bottom) + 16px)" }}>
        <div className="max-w-2xl mx-auto flex items-center gap-2">
          <button
            onClick={() => setIdx(Math.max(0, idx - 1))}
            disabled={idx === 0}
            aria-label="Domanda precedente"
            data-testid="exam-prev-button"
            className="w-12 h-12 rounded-full border border-white/15 flex items-center justify-center disabled:opacity-30 hover:bg-white/10 transition-colors duration-200"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>

          <Sheet open={gridOpen} onOpenChange={setGridOpen}>
            <SheetTrigger asChild>
              <button
                data-testid="exam-open-grid-button"
                className="h-12 flex-1 rounded-full border border-white/15 inline-flex items-center justify-center gap-2 font-mono text-sm hover:bg-white/10 transition-colors duration-200"
              >
                <LayoutGrid className="w-4 h-4" /> {idx + 1}/{questions.length}
              </button>
            </SheetTrigger>
            <SheetContent side="bottom" className="bg-[color:var(--performance-2)] border-white/10 text-white max-h-[70vh] overflow-y-auto">
              <SheetHeader>
                <SheetTitle className="text-white font-extrabold tracking-tight">Naviga le domande</SheetTitle>
              </SheetHeader>
              <div className="flex items-center gap-4 mt-2 mb-4 text-[11px] text-white/60">
                <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded bg-[color:var(--volt)] inline-block" /> risposta data</span>
                <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded border border-[color:var(--volt)] inline-block" /> segnata</span>
              </div>
              <div className="grid grid-cols-8 sm:grid-cols-10 gap-2 pb-4">
                {questions.map((qq, i) => {
                  const done = answers[qq.id] !== undefined;
                  const flg = flagged[qq.id];
                  return (
                    <button
                      key={qq.id}
                      onClick={() => {
                        setIdx(i);
                        setGridOpen(false);
                      }}
                      data-testid="exam-navigation-grid-button"
                      aria-label={`Vai alla domanda ${i + 1}`}
                      className={`h-10 rounded-xl font-mono text-xs border transition-colors duration-200 ${
                        i === idx
                          ? "border-white bg-white text-black font-bold"
                          : done
                          ? "border-transparent bg-[color:var(--volt)] text-[color:var(--ink)] font-bold"
                          : "border-white/15 text-white/70 hover:bg-white/10"
                      } ${flg ? "ring-2 ring-[color:var(--volt)] ring-offset-1 ring-offset-black" : ""}`}
                    >
                      {i + 1}
                    </button>
                  );
                })}
              </div>
            </SheetContent>
          </Sheet>

          <button
            onClick={() => setIdx(Math.min(questions.length - 1, idx + 1))}
            disabled={idx === questions.length - 1}
            aria-label="Domanda successiva"
            data-testid="exam-next-button"
            className="w-12 h-12 rounded-full border border-white/15 flex items-center justify-center disabled:opacity-30 hover:bg-white/10 transition-colors duration-200"
          >
            <ChevronRight className="w-5 h-5" />
          </button>

          <button
            onClick={() => setConfirmOpen(true)}
            disabled={submitting}
            data-testid="exam-submit-block-button"
            className="press h-12 rounded-full bg-[color:var(--volt)] text-[color:var(--ink)] font-extrabold px-5 hover:brightness-95 disabled:opacity-60 transition-all duration-200"
          >
            {submitting ? "…" : "Consegna"}
          </button>
        </div>
        {error && <p className="max-w-2xl mx-auto mt-2 text-xs text-[color:var(--danger)]">{error}</p>}
      </footer>

      {/* Confirm submit */}
      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent className="bg-[color:var(--performance-2)] border-white/10 text-white rounded-3xl">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-extrabold tracking-tight">Consegnare adesso?</AlertDialogTitle>
            <AlertDialogDescription className="text-white/60">
              {unanswered > 0
                ? `Hai ${unanswered} domand${unanswered === 1 ? "a" : "e"} senza risposta: contano come errate.`
                : "Hai risposto a tutte le domande."}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="exam-cancel-submit-button" className="rounded-full bg-transparent border-white/20 text-white hover:bg-white/10 hover:text-white">
              Continua
            </AlertDialogCancel>
            <AlertDialogAction
              data-testid="exam-confirm-submit-button"
              onClick={() => doSubmit(false)}
              className="rounded-full bg-[color:var(--volt)] text-[color:var(--ink)] font-extrabold hover:brightness-95"
            >
              Consegna
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Confirm exit */}
      <AlertDialog open={exitOpen} onOpenChange={setExitOpen}>
        <AlertDialogContent className="bg-[color:var(--performance-2)] border-white/10 text-white rounded-3xl">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-extrabold tracking-tight">Uscire dalla simulazione?</AlertDialogTitle>
            <AlertDialogDescription className="text-white/60">Le risposte di questa sessione andranno perse.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="exam-cancel-exit-button" className="rounded-full bg-transparent border-white/20 text-white hover:bg-white/10 hover:text-white">
              Resta
            </AlertDialogCancel>
            <AlertDialogAction
              data-testid="exam-confirm-exit-button"
              onClick={() => navigate("/simulazioni")}
              className="rounded-full bg-white text-black font-extrabold hover:bg-white/90"
            >
              Esci
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
